import React, { useEffect, useState } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { EnhancedStatCard } from '@/components/shared/EnhancedStatCard';
import { PageTransition } from '@/components/shared/PageTransition';
import { SkeletonLoader } from '@/components/shared/SkeletonLoader';
import { ChartPlaceholder } from '@/components/shared/ChartPlaceholder';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { DashboardFlyer } from '@/components/shared/DashboardFlyer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DollarSign, FileText, Upload, Clock, ArrowRight, TrendingUp, User, Briefcase, Loader2, Calendar, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { dashboardAPI, taxFilingAPI, llcAPI, pricingAPI } from '@/services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    tax_filings: 0,
    llc_formations: 0,
    total_paid: 0,
    documents: 0
  });
  const [recentFilings, setRecentFilings] = useState([]);
  const [currentYearFiling, setCurrentYearFiling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFilingType, setPendingFilingType] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Pricing from API with fallbacks
  const [packagePrices, setPackagePrices] = useState({
    basic: 99,
    standard: 199,
    premium: 349
  });

  // Fetch pricing plans
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const plans = await pricingAPI.getPlans('tax', true, false);
        if (plans && plans.length > 0) {
          const prices = {};
          // Helper to normalize plan name to key
          const normalizeKey = (name) => {
            const lowerName = name.toLowerCase();
            if (lowerName.includes('basic')) return 'basic';
            if (lowerName.includes('standard')) return 'standard';
            if (lowerName.includes('premium')) return 'premium';
            if (lowerName.includes('advanced')) return 'advanced';
            return lowerName.split(' ')[0];
          };
          plans.forEach(plan => {
            const key = normalizeKey(plan.name);
            prices[key] = plan.price;
          });
          if (Object.keys(prices).length > 0) {
            setPackagePrices(prev => ({ ...prev, ...prices }));
          }
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Keep default prices
      }
    };
    fetchPricing();
  }, []);

  // Helper function to get package price
  const getPackagePrice = (packageName) => {
    if (!packageName) return 0;
    return packagePrices[packageName.toLowerCase()] || 0;
  };

  // Calculate tax filing deadline info
  const getTaxDeadlineInfo = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    // Tax deadline is April 15th of current year (or next year if we're past April 15)
    let deadlineYear = currentYear;
    let deadline = new Date(deadlineYear, 3, 15); // Month is 0-indexed, so 3 = April
    
    // If we're past April 15th of current year, the deadline for this tax season has passed
    // The next deadline would be April 15th of next year
    if (today > deadline) {
      // Check if there's a filing for the current tax year
      const hasCurrentYearFiling = recentFilings.some(filing => {
        const filingYear = filing.tax_year || new Date(filing.created_at).getFullYear();
        return filingYear === currentYear || filingYear === currentYear - 1;
      });
      
      if (!hasCurrentYearFiling && stats.tax_filings === 0) {
        return {
          isPastDue: true,
          daysRemaining: 0,
          deadlineDate: deadline,
          message: "Tax deadline has passed!"
        };
      }
      // Move to next year's deadline
      deadlineYear = currentYear + 1;
      deadline = new Date(deadlineYear, 3, 15);
    }
    
    const timeDiff = deadline.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    
    return {
      isPastDue: false,
      daysRemaining,
      deadlineDate: deadline,
      message: daysRemaining <= 30 ? "File soon!" : "Plan ahead"
    };
  };

  const taxDeadlineInfo = getTaxDeadlineInfo();

  // Redirect to landing if no user
  useEffect(() => {
    if (!user) {
      console.log('No user found in ClientDashboard, redirecting to home');
      setTimeout(() => {
        navigate('/');
      }, 100);
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch dashboard statistics
        const statsData = await dashboardAPI.getStats();
        setStats(statsData);
        
        // Fetch recent tax filings
        const filingsData = await taxFilingAPI.getFilings();
        setRecentFilings(filingsData.slice(0, 3)); // Get only 3 most recent
        
        // Check if there's a filing for the current year (individual type only)
        const currentYear = new Date().getFullYear().toString();
        const currentYearFilings = filingsData.filter(filing => 
          filing.tax_year === currentYear && 
          (filing.filing_type === 'individual' || !filing.filing_type) // Default to individual if not specified
        );
        
        // Prefer filing with estimated_refund, or the most recently updated non-draft filing
        const filingWithRefund = currentYearFilings.find(f => f.estimated_refund);
        const nonDraftFiling = currentYearFilings.find(f => f.status !== 'DRAFT' && f.status !== 'draft');
        const existingFiling = filingWithRefund || nonDraftFiling || currentYearFilings[0] || null;
        setCurrentYearFiling(existingFiling);
        
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Dashboard" user={user}>
        <div className="space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse"></div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <SkeletonLoader type="stat-card" count={4} />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Generate dynamic recent activities from actual user data
  const generateRecentActivities = () => {
    const activities = [];
    
    // Add activities from tax filings
    recentFilings.forEach((filing, index) => {
      const statusMap = {
        'DRAFT': 'pending',
        'SUBMITTED': 'in_review',
        'IN_REVIEW': 'in_review',
        'NEED_INFO': 'need_info',
        'READY_FOR_REVIEW': 'in_review',
        'SUBMITTED_TO_IRS': 'completed',
        'COMPLETED': 'completed',
        'REJECTED': 'rejected'
      };
      
      // Determine filing type label
      const filingType = filing.filing_type === 'business' ? 'Business' : 'Individual';
      
      const actionMap = {
        'DRAFT': `${filingType} Tax Filing ${filing.tax_year} - Draft`,
        'SUBMITTED': `${filingType} Tax Filing ${filing.tax_year} - Submitted`,
        'IN_REVIEW': `${filingType} Tax Filing ${filing.tax_year} - Under Review`,
        'NEED_INFO': `${filingType} Tax Filing ${filing.tax_year} - Needs Info`,
        'READY_FOR_REVIEW': `${filingType} Tax Filing ${filing.tax_year} - Ready for Review`,
        'SUBMITTED_TO_IRS': `${filingType} Tax Filing ${filing.tax_year} - Sent to IRS`,
        'COMPLETED': `${filingType} Tax Filing ${filing.tax_year} - Completed`,
        'REJECTED': `${filingType} Tax Filing ${filing.tax_year} - Rejected`
      };
      
      const status = filing.status?.toUpperCase() || 'DRAFT';
      activities.push({
        id: `filing-${filing.id}`,
        filingId: filing.id,
        filingType: filing.filing_type || 'individual',
        action: actionMap[status] || `${filingType} Tax Filing ${filing.tax_year}`,
        date: filing.updated_at || filing.created_at,
        status: statusMap[status] || 'pending',
        type: 'tax_filing'
      });
    });
    
    // Sort by date (most recent first)
    activities.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // If no activities, show welcome message
    if (activities.length === 0) {
      activities.push({
        id: 'welcome',
        action: 'Welcome to Kosha Tax!',
        date: new Date().toISOString(),
        status: 'completed',
        type: 'system'
      });
    }
    
    return activities.slice(0, 5); // Return max 5 activities
  };

  const recentActivities = generateRecentActivities();
  
  // Format date for display
  const formatActivityDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleFilingTypeSelect = (type) => {
    if (type === 'individual') {
      // Check if there's an existing filing for current year
      if (currentYearFiling) {
        // Continue editing existing filing - no confirmation needed
        navigate(`/filing/start?id=${currentYearFiling.id}`);
      } else {
        // Starting new individual filing - show confirmation dialog
        setPendingFilingType(type);
        setShowConfirmDialog(true);
      }
    } else if (type === 'business') {
      // Navigate directly to business dashboard (popup will be shown there)
      navigate('/business/dashboard');
    }
  };

  const handleConfirmNewFiling = () => {
    setShowConfirmDialog(false);
    if (pendingFilingType === 'individual') {
      navigate('/filing/start');
    } else if (pendingFilingType === 'business') {
      navigate('/business/filing/start');
    }
    setPendingFilingType(null);
  };

  const handleCancelNewFiling = () => {
    setShowConfirmDialog(false);
    setPendingFilingType(null);
  };

  // Handle Pay Now button click
  const handlePayNow = async (filing) => {
    if (!filing || !filing.id) {
      toast.error('No filing selected for payment');
      return;
    }

    try {
      setProcessingPayment(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${backendUrl}/api/payments/create-checkout`,
        {
          filing_id: filing.id,
          filing_type: 'tax',
          origin_url: originUrl
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Safety check: if no user, show loading
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const userName = user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : user.email;

  return (
    <DashboardLayout role={user.role} title="Dashboard" user={user}>
      <PageTransition>
        <div className="space-y-6">
          <Breadcrumbs items={[{ label: 'Dashboard' }]} />

          {/* Welcome Section with Flyer on Right */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-shrink-0"
            >
              <h2 className="text-3xl font-bold text-foreground">Welcome back, {userName}!</h2>
              <p className="mt-1 text-muted-foreground">Choose your filing type to get started</p>
            </motion.div>

            {/* Promotional Flyer - Right side on desktop, below on mobile */}
            <DashboardFlyer className="md:max-w-md lg:max-w-lg flex-shrink-0" />
          </div>

        {/* Filing Type Selector */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card 
            className="tax-card cursor-pointer hover:ring-2 hover:ring-primary transition-all"
            onClick={() => handleFilingTypeSelect('individual')}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-primary/10 p-3">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-foreground mb-2">Individual Tax Filing</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    File your personal income taxes, claim deductions, and get your refund
                  </p>
                  {currentYearFiling ? (
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">
                        {new Date().getFullYear()} filing: <span className="font-semibold capitalize">{currentYearFiling.status.toLowerCase().replace('_', ' ')}</span>
                      </div>
                      {/* Agent Info - Show when agent is assigned */}
                      {currentYearFiling.agent_name && (
                        <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                            {currentYearFiling.agent_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-blue-800 dark:text-blue-200 font-medium">Your Tax Professional</p>
                            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">{currentYearFiling.agent_name}</p>
                          </div>
                        </div>
                      )}
                      {currentYearFiling.estimated_refund && (
                        <div className="text-sm font-semibold text-green-600">
                          Estimated Refund: ${Number(currentYearFiling.estimated_refund).toLocaleString()}
                        </div>
                      )}
                      {currentYearFiling.balance_due && (
                        <div className="text-sm font-semibold text-red-600">
                          Balance Due: ${Number(currentYearFiling.balance_due).toLocaleString()}
                        </div>
                      )}
                      {/* Discount and Total Section - Only show when status is READY_FOR_REVIEW or later */}
                      {(currentYearFiling.discount_amount || currentYearFiling.selected_package) && 
                       ['READY_FOR_REVIEW', 'SUBMITTED_TO_IRS', 'COMPLETED'].includes(currentYearFiling.status?.toUpperCase()) && (
                        <div className="mt-2 p-2 bg-muted/50 rounded-md space-y-1">
                          {currentYearFiling.selected_package && (
                            <div className="text-xs flex justify-between">
                              <span className="text-muted-foreground">Package ({currentYearFiling.selected_package}):</span>
                              <span className="font-medium">
                                ${getPackagePrice(currentYearFiling.selected_package)}
                              </span>
                            </div>
                          )}
                          {currentYearFiling.discount_amount > 0 && (
                            <div className="text-xs flex justify-between text-orange-600">
                              <span>Discount:</span>
                              <span className="font-medium">-${Number(currentYearFiling.discount_amount).toLocaleString()}</span>
                            </div>
                          )}
                          {currentYearFiling.selected_package && (
                            <div className="text-sm flex justify-between font-semibold border-t pt-1 mt-1">
                              <span>Total:</span>
                              <span className="text-primary">
                                ${Math.max(0, getPackagePrice(currentYearFiling.selected_package) - 
                                  (currentYearFiling.discount_amount || 0)).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {/* Payment Status */}
                          {currentYearFiling.payment_status === 'paid' && (
                            <div className="text-sm flex justify-between items-center text-green-600 border-t pt-1 mt-1">
                              <span>Payment Status:</span>
                              <span className="font-semibold flex items-center gap-1">
                                <DollarSign className="h-3 w-3" /> Paid
                              </span>
                            </div>
                          )}
                          {currentYearFiling.payment_status === 'failed' && (
                            <div className="text-sm flex justify-between items-center text-red-600 border-t pt-1 mt-1">
                              <span>Payment Status:</span>
                              <span className="font-semibold">Failed</span>
                            </div>
                          )}
                        </div>
                      )}
                      {(currentYearFiling.status === 'DRAFT' || currentYearFiling.status === 'draft' || 
                        currentYearFiling.status === 'NEED_INFO' || currentYearFiling.status === 'need_info') ? (
                        <Button 
                          className="w-full" 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/filing/start?id=${currentYearFiling.id}`);
                          }}
                        >
                          {currentYearFiling.status.toUpperCase() === 'NEED_INFO' ? 'Update Filing' : 'Continue Filing'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      ) : (
                        <div className="space-y-2">
                          <Button 
                            className="w-full" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/filing/start?id=${currentYearFiling.id}&mode=view`);
                            }}
                          >
                            View {new Date().getFullYear()} Filing
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                          {/* Only show Pay Now button if status is READY_FOR_REVIEW or later and not already paid */}
                          {currentYearFiling.selected_package && 
                           currentYearFiling.payment_status !== 'paid' && 
                           ['READY_FOR_REVIEW', 'SUBMITTED_TO_IRS', 'COMPLETED'].includes(currentYearFiling.status?.toUpperCase()) && (
                            <Button 
                              className="w-full bg-green-600 hover:bg-green-700 text-white" 
                              disabled={processingPayment}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePayNow(currentYearFiling);
                              }}
                            >
                              {processingPayment ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <DollarSign className="mr-2 h-4 w-4" />
                                  Pay Now
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button className="w-full">
                      Start Individual Filing
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="tax-card cursor-pointer hover:ring-2 hover:ring-secondary transition-all"
            onClick={() => handleFilingTypeSelect('business')}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-secondary/10 p-3">
                  <Briefcase className="h-8 w-8 text-secondary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-foreground mb-2">Business Tax Filing</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    File business returns for LLC, S-Corp, Partnership, or Schedule C
                  </p>
                  <Button className="w-full bg-secondary hover:bg-secondary-dark text-secondary-foreground">
                    Start Business Filing
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid gap-6 md:grid-cols-2">
          <EnhancedStatCard
            title="Tax Filings"
            value={stats.tax_filings.toString()}
            icon={FileText}
            gradient="from-blue-50 to-white dark:from-blue-900/20 dark:to-background"
            iconColor="text-blue-600 dark:text-blue-400"
            iconBg="bg-blue-100 dark:bg-blue-900/30"
            delay={0}
          />
          <EnhancedStatCard
            title="LLC Formations"
            value={stats.llc_formations.toString()}
            icon={Briefcase}
            gradient="from-purple-50 to-white dark:from-purple-900/20 dark:to-background"
            iconColor="text-purple-600 dark:text-purple-400"
            iconBg="bg-purple-100 dark:bg-purple-900/30"
            delay={0.1}
          />
        </div>

        {/* Tax Filing Deadline Card */}
        <Card className={`card-elevated ${taxDeadlineInfo.isPastDue ? 'border-red-300 dark:border-red-800' : taxDeadlineInfo.daysRemaining <= 30 ? 'border-amber-300 dark:border-amber-800' : 'border-green-300 dark:border-green-800'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${taxDeadlineInfo.isPastDue ? 'bg-red-100 dark:bg-red-900/30' : taxDeadlineInfo.daysRemaining <= 30 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                  {taxDeadlineInfo.isPastDue ? (
                    <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                  ) : (
                    <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Tax Filing Deadline</h3>
                  <p className="text-sm text-muted-foreground">
                    Due: April 15, {taxDeadlineInfo.deadlineDate.getFullYear()}
                  </p>
                </div>
              </div>
              <div className="text-right">
                {taxDeadlineInfo.isPastDue ? (
                  <div>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">Due Date Crossed</p>
                    <p className="text-sm text-red-500">File as soon as possible</p>
                  </div>
                ) : (
                  <div>
                    <p className={`text-3xl font-bold ${taxDeadlineInfo.daysRemaining <= 30 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                      {taxDeadlineInfo.daysRemaining}
                    </p>
                    <p className="text-sm text-muted-foreground">days remaining</p>
                  </div>
                )}
              </div>
            </div>
            {(taxDeadlineInfo.isPastDue || taxDeadlineInfo.daysRemaining <= 60) && stats.tax_filings === 0 && (
              <div className="mt-4 pt-4 border-t">
                <Button 
                  className="w-full" 
                  variant={taxDeadlineInfo.isPastDue ? "destructive" : "default"}
                  onClick={() => navigate('/filing/start')}
                >
                  {taxDeadlineInfo.isPastDue ? "File Now - Avoid Penalties" : "Start Your Tax Filing"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Quick Actions */}
          <Card className="card-elevated lg:col-span-1">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-between" 
                onClick={() => navigate('/filing/start')}
              >
                Continue Filing
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => {
                  if (currentYearFiling) {
                    // Go to step 5 (Upload Documents) with the filing ID
                    navigate(`/filing/start?id=${currentYearFiling.id}&step=5`);
                  } else {
                    // If no filing exists, go to regular documents page
                    navigate('/documents');
                  }
                }}
              >
                Upload Documents
                <Upload className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/filing-status')}
              >
                Check Status
                <Clock className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="card-elevated lg:col-span-2">
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className={`flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50 ${activity.type === 'tax_filing' ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (activity.type === 'tax_filing' && activity.filingId) {
                        if (activity.filingType === 'business') {
                          navigate(`/business/filing-status`);
                        } else {
                          navigate(`/filing-status`);
                        }
                      }
                    }}
                  >
                    <div>
                      <p className="font-medium text-foreground">{activity.action}</p>
                      <p className="text-sm text-muted-foreground">{formatActivityDate(activity.date)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={activity.status} />
                      {activity.type === 'tax_filing' && (
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                ))}
                {recentActivities.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No recent activity</p>
                    <p className="text-sm mt-1">Start a tax filing to see your activity here</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      </PageTransition>

      {/* Confirmation Dialog for New Tax Filing */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingFilingType === 'individual' 
                ? 'Certification & Consent - Individual Tax Filing' 
                : 'Certification & Consent - Business Tax Filing'}
            </AlertDialogTitle>
            <div className="space-y-4 text-sm text-left">
              <p className="font-medium text-foreground">
                Please read and acknowledge the following before proceeding:
              </p>
              
              <div className="space-y-3 bg-muted/50 p-4 rounded-lg border text-muted-foreground">
                <p>
                  <span className="font-medium">1.</span> I certify that all documents and information I submit are true, complete, and accurate to the best of my knowledge.
                </p>
                <p>
                  <span className="font-medium">2.</span> I authorize Kosha Tax Consultants to review, prepare, and file my tax return using the information I provide.
                </p>
                <p>
                  <span className="font-medium">3.</span> I understand that my documents contain sensitive personal and financial data and consent to their secure storage and processing.
                </p>
                <p>
                  <span className="font-medium">4.</span> I acknowledge that tax refunds, credits, and outcomes are determined by the IRS and applicable authorities and are not guaranteed.
                </p>
                <p>
                  <span className="font-medium">5.</span> I agree to respond promptly to requests for additional information. Delays caused by incomplete or missing documents are my responsibility.
                </p>
                <p>
                  <span className="font-medium">6.</span> I understand that Kosha Tax Consultants is not responsible for penalties, interest, or losses resulting from incorrect, incomplete, or misleading information provided by me.
                </p>
                <p>
                  <span className="font-medium">7.</span> I consent to receive tax-related communications and documents electronically.
                </p>
                <p>
                  <span className="font-medium">8.</span> I agree that submitted documents may be retained for compliance, audit, and regulatory purposes.
                </p>
                <p>
                  <span className="font-medium">9.</span> I understand that submission of documents does not establish a client relationship until services are confirmed and payment is completed.
                </p>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">Data Privacy & Deletion Request</p>
                <p className="text-blue-800 dark:text-blue-200">
                  I understand that I may request deletion of my personal data, subject to legal and regulatory retention requirements, by contacting <a href="mailto:support@koshatax.com" className="underline font-medium">support@koshatax.com</a>.
                </p>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={handleCancelNewFiling}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmNewFiling}>I Agree & Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
